import { useRef, useEffect } from 'react'

/* ───────────────────────────────────────────────────────────────
 *  PRIVACY POLICY — bottom sheet
 *
 *  Mobile-first fix:
 *   • Uses dvh (not vh) so the sheet respects mobile browser chrome
 *   • Honors safe-area inset at the bottom (notch / home indicator)
 *   • The ENTIRE top bar (handle + close row) is the drag area, not
 *     just the tiny 48px handle — far easier to grab on a phone
 *   • Big, obvious close button that always works
 *   • Tapping the dimmed backdrop closes it
 *   • Drag down past threshold → closes; otherwise snaps back
 * ─────────────────────────────────────────────────────────────── */

const SHEET_CONFIG = {
  maxWidth: '600px',
  // Height: tall but never taller than the visible viewport.
  heightMobile: '90dvh',
  heightDesktop: '85vh',
  radius: '22px',
  closeThreshold: 110,          // px dragged down before it closes (slow drag)
  velocityClose: 0.5,           // px/ms — a fast flick closes even on a short drag
  topResistance: 0.55,          // rubber-band: resistance if dragged UPward past the top
  settleSpring: 'transform 0.42s cubic-bezier(0.22, 1, 0.36, 1)',  // weighted spring settle
  closeEase: 'transform 0.3s cubic-bezier(0.4, 0, 1, 1)',          // accelerate out when closing
}

function PrivacyPolicy({ show, onClose }) {
  const sheetRef = useRef(null)
  const dragging = useRef(false)
  const startY = useRef(0)
  const offsetY = useRef(0)
  // Velocity tracking — sample the last move so a flick can dismiss.
  const lastY = useRef(0)
  const lastT = useRef(0)
  const velocity = useRef(0)

  // Animate in on open
  useEffect(() => {
    if (show && sheetRef.current) {
      // start just below, then settle to 0
      sheetRef.current.style.transition = 'none'
      sheetRef.current.style.transform = 'translateY(100%)'
      // force reflow so the transition runs
      void sheetRef.current.offsetHeight
      sheetRef.current.style.transition = 'transform 0.42s cubic-bezier(0.22, 1, 0.36, 1)'
      sheetRef.current.style.transform = 'translateY(0)'
    }
  }, [show])

  const beginDrag = (clientY) => {
    dragging.current = true
    startY.current = clientY
    offsetY.current = 0
    lastY.current = clientY
    lastT.current = performance.now()
    velocity.current = 0
    if (sheetRef.current) sheetRef.current.style.transition = 'none'
  }

  const moveDrag = (clientY) => {
    if (!dragging.current) return
    const dy = clientY - startY.current

    // Track instantaneous velocity (px/ms) for flick-to-dismiss.
    const now = performance.now()
    const dt = now - lastT.current
    if (dt > 0) velocity.current = (clientY - lastY.current) / dt
    lastY.current = clientY
    lastT.current = now

    // Downward drag moves 1:1. Dragging UP past the top gets heavy
    // rubber-band resistance so the sheet feels anchored, not loose.
    let visual
    if (dy >= 0) {
      visual = dy
    } else {
      visual = dy * SHEET_CONFIG.topResistance
    }
    offsetY.current = dy
    if (sheetRef.current) sheetRef.current.style.transform = `translateY(${visual}px)`
  }

  const endDrag = () => {
    if (!dragging.current) return
    dragging.current = false
    if (!sheetRef.current) return

    const draggedFar = offsetY.current > SHEET_CONFIG.closeThreshold
    const flickedDown = velocity.current > SHEET_CONFIG.velocityClose

    if (draggedFar || flickedDown) {
      // Accelerate out — duration scales slightly with how fast the flick was.
      sheetRef.current.style.transition = SHEET_CONFIG.closeEase
      sheetRef.current.style.transform = 'translateY(100%)'
      setTimeout(onClose, 300)
    } else {
      // Spring back to rest — weighted settle, not a linear snap.
      sheetRef.current.style.transition = SHEET_CONFIG.settleSpring
      sheetRef.current.style.transform = 'translateY(0)'
    }
    offsetY.current = 0
    velocity.current = 0
  }

  // Pointer events cover both mouse + touch on modern browsers.
  const onPointerDown = (e) => { beginDrag(e.clientY); e.currentTarget.setPointerCapture?.(e.pointerId) }
  const onPointerMove = (e) => moveDrag(e.clientY)
  const onPointerUp = () => endDrag()

  // Touch fallback (some mobile browsers fire touch better than pointer
  // inside a scroll container). These only run on the drag bar.
  const onTouchStart = (e) => beginDrag(e.touches[0].clientY)
  const onTouchMove = (e) => { moveDrag(e.touches[0].clientY) }
  const onTouchEnd = () => endDrag()

  if (!show) return null

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'var(--overlay-medium)',
        zIndex: 9999,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        ref={sheetRef}
        style={{
          background: 'var(--color-bg-card-heavy)',
          backdropFilter: 'blur(var(--glass-blur))',
          WebkitBackdropFilter: 'blur(var(--glass-blur))',
          borderRadius: `${SHEET_CONFIG.radius} ${SHEET_CONFIG.radius} 0 0`,
          width: '100%', maxWidth: SHEET_CONFIG.maxWidth,
          height: SHEET_CONFIG.heightMobile,
          maxHeight: SHEET_CONFIG.heightMobile,
          display: 'flex', flexDirection: 'column',
          border: 'var(--border-input)', borderBottom: 'none',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── DRAG BAR (whole strip is draggable + has the close button) ── */}
        <div
          style={{
            flexShrink: 0,
            cursor: 'grab',
            touchAction: 'none',
            userSelect: 'none',
            paddingTop: '8px',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* grab handle */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '40px', height: '5px', borderRadius: '2.5px', background: 'rgba(255,255,255,0.28)' }} />
          </div>

          {/* close button row */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 14px 4px' }}>
            <button
              onClick={onClose}
              onPointerDown={(e) => e.stopPropagation()}   /* don't start a drag from the button */
              onTouchStart={(e) => e.stopPropagation()}
              style={{
                width: '40px', height: '40px', borderRadius: '50%',
                background: 'rgba(255,255,255,0.14)',
                border: '1px solid rgba(255, 255, 255, 0.30)',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', padding: 0,
                WebkitTapHighlightColor: 'transparent',
              }}
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.19336 8.45508C9.39258 8.6543 9.39258 8.98828 9.19336 9.19336C8.99414 9.39844 8.64844 9.39258 8.44922 9.19336L4.69922 5.44336L0.949219 9.19336C0.75 9.39258 0.410156 9.39258 0.205078 9.19336C0 8.99414 0.00585938 8.6543 0.205078 8.45508L3.95508 4.69922L0.205078 0.949219C0.00585938 0.75 0.00585938 0.416016 0.205078 0.210938C0.404297 0 0.75 0.00585938 0.949219 0.210938L4.69922 3.96094L8.44922 0.210938C8.6543 0 8.98828 0.00585938 9.19336 0.210938C9.39844 0.410156 9.39258 0.75 9.19336 0.949219L5.44336 4.69922L9.19336 8.45508Z" fill="white"/>
              </svg>
            </button>
          </div>
        </div>

        {/* ── SCROLLABLE CONTENT ── */}
        <div
          className="no-sb"
          style={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: '4px 24px calc(40px + env(safe-area-inset-bottom, 0px))',
            minHeight: 0,
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: 'var(--weight-bold)', color: 'var(--color-text-primary)', marginBottom: '17px' }}>Airlog Privacy Policy</h2>
          <Section title="Information We Collect">
            <P>We collect the following information:</P>
            <P>Email address (for account creation and login)</P>
            <P>Content you create, including:</P>
            <P>Text logs</P>
            <P>Photos and videos</P>
            <P>Voice recordings</P>
            <P>Files you upload</P>
          </Section>
          <Section title="How We Use Your Information">
            <P>We use your information to:</P>
            <P>Provide and operate AirLog</P>
            <P>Store and display your logs</P>
            <P>Sync your data across devices</P>
            <P>Authenticate your account</P>
          </Section>
          <Section title="Data Storage">
            <P>Your data is securely stored using Supabase.</P>
            <P>We take reasonable steps to protect your information, but no system is completely secure.</P>
          </Section>
          <Section title="Data Sharing">
            <P>We do not sell, trade, or share your personal data with third parties for advertising purposes.</P>
            <P>Your content remains private to your account.</P>
          </Section>
          <Section title="Your Rights">
            <P>You can:</P>
            <P indent={1}>Access your data at any time</P>
            <P indent={1}>Delete your logs</P>
            <P indent={1}>Request account and data deletion</P>
          </Section>
          <Section title="Third-Party Services">
            <P>We use third-party services to operate the app, including:</P>
            <P>Supabase (data storage and authentication)</P>
          </Section>
          <Section title="Contact">
            <P>If you have questions, contact us at:</P>
            <p style={{ fontSize: '19px', color: 'var(--color-text-link)', lineHeight: 'var(--leading-loose)', margin: 0 }}>your@email.com</p>
          </Section>
        </div>
      </div>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <>
      <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-text-primary)', marginBottom: '8px', marginTop: '16px' }}>{title}</h3>
      {children}
    </>
  )
}

function P({ children, indent = 0 }) {
  return (
    <p style={{ fontSize: '19px', color: '#c0c0c0', lineHeight: 'var(--leading-loose)', margin: '0 0 4px', paddingLeft: `${indent * 16}px` }}>{children}</p>
  )
}

export default PrivacyPolicy